import { useState } from 'react'
import { FormField } from '@/components/FormField'
import { useToast } from '@/hooks/useToast'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'
import { CreateModal, useInvalidate } from './WorkforceModals'
import {
  createBenefitPlan, createLeaveType, createHolidayCalendar, createSalaryStructure, createPayGrade,
} from './extras'

const PLAN_KINDS = ['medical', 'dental', 'vision', 'life', 'disability', 'hsa', 'fsa', '401k', 'cobra', 'voluntary']

export function BenefitPlanModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const regional = useRegionalDefaults()
  const [f, setF] = useState({ plan_code: '', plan_name: '', plan_kind: 'medical', carrier: '', employer_share_pct: '', employee_share_pct: '', pretax: true })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="New benefit plan" onSubmit={async () => {
      if (!f.plan_code || !f.plan_name) { toast({ title: 'Plan code and name required', variant: 'destructive' }); throw new Error('validation') }
      await createBenefitPlan({
        plan_code: f.plan_code, plan_name: f.plan_name, plan_kind: f.plan_kind, carrier: f.carrier || null,
        currency: regional.currency,
        employer_share_pct: f.employer_share_pct ? Number(f.employer_share_pct) : null,
        employee_share_pct: f.employee_share_pct ? Number(f.employee_share_pct) : null,
        pretax: f.pretax, is_active: true,
      })
      invalidate(); toast({ title: 'Benefit plan created' })
    }}>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Plan code" value={f.plan_code} onChange={(e) => setF({ ...f, plan_code: e.target.value })} />
        <FormField label="Plan name" value={f.plan_name} onChange={(e) => setF({ ...f, plan_name: e.target.value })} />
        <FormField label="Kind" control={
          <select value={f.plan_kind} onChange={(e) => setF({ ...f, plan_kind: e.target.value })}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
            {PLAN_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        } />
        <FormField label="Carrier" value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })} />
        <FormField label="Employer share %" inputMode="decimal" value={f.employer_share_pct} onChange={(e) => setF({ ...f, employer_share_pct: e.target.value.replace(/[^\d.]/g, '') })} />
        <FormField label="Employee share %" inputMode="decimal" value={f.employee_share_pct} onChange={(e) => setF({ ...f, employee_share_pct: e.target.value.replace(/[^\d.]/g, '') })} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.pretax} onChange={(e) => setF({ ...f, pretax: e.target.checked })} /> Pre-tax deduction
      </label>
    </CreateModal>
  )
}

const ACCRUAL_PERIODS = ['per_pay_period', 'monthly', 'quarterly', 'annually', 'per_hour_worked', 'none']

export function LeaveTypeModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const [f, setF] = useState({ slug: '', label: '', is_paid: true, accrual_rate_hours: '0', accrual_period: 'monthly', requires_approval: true })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="New leave type" onSubmit={async () => {
      if (!f.slug || !f.label) { toast({ title: 'Slug and label required', variant: 'destructive' }); throw new Error('validation') }
      await createLeaveType({ ...f, accrual_rate_hours: Number(f.accrual_rate_hours) || 0 })
      invalidate(); toast({ title: 'Leave type created' })
    }}>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Slug" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.replace(/[^a-z0-9_]/g, '').toLowerCase() })} />
        <FormField label="Label" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <FormField label="Accrual rate (hours)" inputMode="decimal" value={f.accrual_rate_hours} onChange={(e) => setF({ ...f, accrual_rate_hours: e.target.value.replace(/[^\d.]/g, '') })} />
        <FormField label="Accrual period" control={
          <select value={f.accrual_period} onChange={(e) => setF({ ...f, accrual_period: e.target.value })}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
            {ACCRUAL_PERIODS.map((k) => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
          </select>
        } />
      </div>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_paid} onChange={(e) => setF({ ...f, is_paid: e.target.checked })} /> Paid</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.requires_approval} onChange={(e) => setF({ ...f, requires_approval: e.target.checked })} /> Requires approval</label>
      </div>
    </CreateModal>
  )
}

export function HolidayCalendarModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const regional = useRegionalDefaults()
  const [f, setF] = useState({ name: '', country: regional.country, state_region: '' })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="New holiday calendar" onSubmit={async () => {
      if (!f.name) { toast({ title: 'Name required', variant: 'destructive' }); throw new Error('validation') }
      await createHolidayCalendar({ name: f.name, country: f.country, state_region: f.state_region || null })
      invalidate(); toast({ title: 'Calendar created' })
    }}>
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <FormField label="Country" maxLength={2} value={f.country} onChange={(e) => setF({ ...f, country: e.target.value.toUpperCase().slice(0, 2) })} />
        <FormField label="State" maxLength={2} value={f.state_region} onChange={(e) => setF({ ...f, state_region: e.target.value.toUpperCase().slice(0, 2) })} />
      </div>
    </CreateModal>
  )
}

export function SalaryStructureModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const regional = useRegionalDefaults()
  const [f, setF] = useState({ name: '', description: '', is_template: true })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="New salary structure" onSubmit={async () => {
      if (!f.name) { toast({ title: 'Name required', variant: 'destructive' }); throw new Error('validation') }
      await createSalaryStructure({ name: f.name, description: f.description || null, currency: regional.currency, is_template: f.is_template })
      invalidate(); toast({ title: 'Structure created' })
    }}>
      <FormField label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <FormField label="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_template} onChange={(e) => setF({ ...f, is_template: e.target.checked })} /> Template</label>
    </CreateModal>
  )
}

export function PayGradeModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const regional = useRegionalDefaults()
  const [f, setF] = useState({ grade_code: '', label: '', min: '', mid: '', max: '' })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="New pay grade" onSubmit={async () => {
      if (!f.grade_code || !f.label || !f.min || !f.mid || !f.max) { toast({ title: 'All fields required', variant: 'destructive' }); throw new Error('validation') }
      await createPayGrade({
        grade_code: f.grade_code, label: f.label, currency: regional.currency,
        min_minor_units: Math.round(Number(f.min) * 100),
        mid_minor_units: Math.round(Number(f.mid) * 100),
        max_minor_units: Math.round(Number(f.max) * 100),
      })
      invalidate(); toast({ title: 'Pay grade created' })
    }}>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Grade code" value={f.grade_code} onChange={(e) => setF({ ...f, grade_code: e.target.value })} />
        <FormField label="Label" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <FormField label="Min" inputMode="decimal" value={f.min} onChange={(e) => setF({ ...f, min: e.target.value.replace(/[^\d.]/g, '') })} />
        <FormField label="Mid" inputMode="decimal" value={f.mid} onChange={(e) => setF({ ...f, mid: e.target.value.replace(/[^\d.]/g, '') })} />
        <FormField label="Max" inputMode="decimal" value={f.max} onChange={(e) => setF({ ...f, max: e.target.value.replace(/[^\d.]/g, '') })} />
      </div>
    </CreateModal>
  )
}
